import { ChangeEventHandler, useContext, useEffect, useRef, useState } from 'react';
import ErrorAlert from '../../common/components/ErrorAlert';
import Button from '../../common/components/button';
import Checkbox from '../../common/components/checkbox';
import DateFields from '../../common/components/DateFields';
import Div from '../../common/components/div';
import H2 from '../../common/components/h2';
import H3 from '../../common/components/h3';
import SecurityNoticeDialog from '../../common/components/SecurityNoticeDialog';
import StackingButtons from '../../common/components/StackingButtons';
import TranslatedSpan from '../../common/components/TranslatedSpan';
import ValidationAlertMessage from '../../common/components/ValidationAlertMessage';
import { isValidYear } from '../../common/functions/isValidYear';
import translate from '../../common/functions/translate';
import { useConversion } from '../../common/hooks/use.analytics';
import useTrait from '../../common/hooks/useTrait';
import is from '../../common/objects/is';
import { DateObject } from '../../common/objects/models/date.field.value';
import { hasInvalidField } from '../../common/functions/getApiErrorMessage';
import LastNameSection from '../../common/components/LastNameSection';
import { Link } from 'react-router-dom';
import { AllFieldsRequired } from '../../common/components/AllFieldsRequired';
import useCreateAccountApi from '../../common/hooks/useCreateAccountApi';
import { createOrUnlockAccountData } from '../createAccountContext';
import { scrollIntoViewAndFocus } from '../../common/functions/scrollIntoViewAndFocus';
import { TextField } from '@cmsgov/ds-medicare-gov';
import { HorizontalRule } from '../../common/components/HorizontalRule';

export type BasicInformationErrorKeys = 'getHelp' | 'checkInformation' | 'processReenter' | 'processInconvenience';
export type BasicInformationErrorHeaderKeys = 'cantFind' | 'completeItems' | 'alreadyExists' | 'cantProcess' | 'alreadyHaveAccount';
export type BasicInformationErrorListKeys = 'invalidLastName' | 'invalidDOB' | 'invalidZipOrCity' | 'invalidEmail';
interface YourMedicareInformationProps {
    testInitialErrorState?: BasicInformationErrorKeys & BasicInformationErrorListKeys
    testHeaderKey?: BasicInformationErrorHeaderKeys
}

const YourBasicInformation = (props: YourMedicareInformationProps) => {
    const [certificationCheckedRef, setCertificationCheckedRef] = useState<HTMLElement | null>(null);
    const [confirmEmailAddressRef, setConfirmEmailAddressRef] = useState<HTMLElement | null>(null);
    const [dayRef, setDayRef] = useState<HTMLElement | null>(null);
    const [emailAddressRef, setEmailAddressRef] = useState<HTMLElement | null>(null);
    const [lastNameRef, setLastNameRef] = useState<HTMLElement | null>(null);
    const [monthRef, setMonthRef] = useState<HTMLElement | null>(null);
    const [yearRef, setYearRef] = useState<HTMLElement | null>(null);
    const [zipCodeRef, setZipCodeRef] = useState<HTMLElement | null>(null);
    const confirmEmailAddressError = useTrait(false);
    const dateFieldKey = useTrait(new Date().getTime());
    const emailAddressError = useTrait(false);
    const lastNameError = useTrait(false);
    const validateEntireForm = useTrait(false);
    const zipCodeOrCityError = useTrait(false);
    const [isLoading, setIsLoading] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [certificationTouched, setCertificationTouched] = useState(false);
    const errorContainerRef = useRef<HTMLDivElement | null>(null);

    const api = useCreateAccountApi();
    const { data, updateData, stepBackward, stepForward } = useContext(createOrUnlockAccountData);
    const {
        suffix,
        lastName,
        dateOfBirth,
        zipCodeOrCity,
        emailAddress,
        noEmailAddressChecked,
        confirmEmailAddress ,
        certificationChecked
    } = data;

    const conversion = useConversion('sign_up', 'sign_up');

    const [errorMessage, setErrorMessage] = useState<JSX.Element | string | null>("");
    const [errorKey, setErrorKey] = useState<BasicInformationErrorKeys | null>(props.testInitialErrorState || null);
    const [errorListKey, setErrorListKey] = useState<BasicInformationErrorListKeys | null>(props.testInitialErrorState || null);
    const [errorKeyHeader, setErrorKeyHeader] = useState<BasicInformationErrorHeaderKeys | null>(props.testHeaderKey || null);
    const [errorAlertHeading, setErrorAlertHeading] = useState("");
    const [apiErrorMessageList, setApiErrorMessageList] = useState<JSX.Element[]>([]);

    const [analyticsErrorKey, setAnalyticsErrorKey] = useState<BasicInformationErrorKeys | BasicInformationErrorListKeys | null>(null);

    const currentListKey = useRef<string | null>(null);
    const header = translate('enter_your_information');

    useEffect(() => {
        const errorHeaders: {
            [key in BasicInformationErrorHeaderKeys]: string;
        } = {
            cantFind: translate("we_cant_find_you"),
            completeItems: translate("complete_items_to_create_account"),
            alreadyExists: translate("looks_like_already_have_account"),
            alreadyHaveAccount: translate('looks_like_already_have_account'),
            cantProcess: translate("we_cant_process_request")
        };
        if (errorKeyHeader) {
            setErrorAlertHeading(errorKeyHeader ? errorHeaders[errorKeyHeader] : "");
        }
    }, [setErrorAlertHeading, errorKeyHeader]);

    useEffect(() => {
        const errorKeyToMessage: {
            [key in BasicInformationErrorKeys]: string | JSX.Element
        } = {
            checkInformation: translate("check_information_try_again"),
            processReenter: translate("we_cant_process_request_reenter_and_try_again"),
            processInconvenience: translate("we_are_sorry_for_inconvenience_try_creating_account_later"),
            getHelp: <>
                <Link to="/forgot-username-and-password/">
                    {translate('get_help')}
                </Link> {' '}
                {translate("do_not_remember_your_username_and_password")}
            </>
        }
        setErrorMessage(errorKey ? errorKeyToMessage[errorKey] : null);
    }, [errorKey, setErrorMessage]);

    useEffect(() => {
        const errorKeyToMessage: {
            [key in BasicInformationErrorListKeys]: JSX.Element
        } = {
            invalidLastName: <>
                <ValidationAlertMessage
                    description={translate("check_last_name")}
                    fieldReference={lastNameRef}
                    linkText={translate("last_name")}
                    componentHeading={header}
                />
            </>,
            invalidDOB: <>
                <ValidationAlertMessage
                    description={translate("enter_your_dob")}
                    fieldReference={monthRef}
                    linkText={translate("date_of_birth")}
                    componentHeading={header}
                />
            </>,
            invalidZipOrCity: <>
                <ValidationAlertMessage
                    description={translate("enter_your_zipcode_or_city")}
                    fieldReference={zipCodeRef}
                    linkText={translate("zip_code_or_city")}
                    componentHeading={header}
                />
            </>,
            invalidEmail: <>
                <ValidationAlertMessage
                    description={translate('enter_email_or_select_do_not_have')}
                    fieldReference={emailAddressRef}
                    linkText={translate("email")}
                    componentHeading={header}
                />
            </>
        }

        if (currentListKey.current === errorListKey) {
            return;
        }
        if (errorListKey && errorListKey in errorKeyToMessage) {
            setApiErrorMessageList([errorKeyToMessage[errorListKey], ...apiErrorMessageList]);
        } else {
            setApiErrorMessageList([]);
        }
        currentListKey.current = errorListKey;
    }, [errorListKey, apiErrorMessageList, lastNameRef, monthRef, zipCodeRef, emailAddressRef, header]);

    useEffect(() => {
        const errorStrings: {
            [key in string]: string;
        } = {
            checkInformation: "We can't find you in our records. Check the information you entered and try again.",
            processReenter: "We couldn't process your request. Please re-enter your information and try again.",
            processInconvenience: "We can't process your request at this time. We're sorry for any inconvenience. Try creating your account later.",
            getHelp: "Get help if you don't remember your username or password.",
            invalidLastName: "Check your last name and try again.",
            invalidDOB: "Enter your date of birth.",
            invalidZipOrCity: "Enter your ZIP code or city.",
            invalidEmail: 'Enter your email address or select "I don\'t have an email address."'
        };
        if (analyticsErrorKey) {
            conversion.failAPIIntermediate(errorStrings[analyticsErrorKey]);
        }
    }, [analyticsErrorKey, conversion]);

    const callCreateAccount = async () => {
        const lastNameWithSuffix = suffix ? `${lastName} ${suffix}` : lastName;
        const response = await api.step2(lastNameWithSuffix, dateOfBirth, zipCodeOrCity, emailAddress);
        setIsLoading(false);
        const statusCode = response?.status;
        if(statusCode !== 204){
            scrollIntoViewAndFocus(errorContainerRef.current);
        }
        validateEntireForm.set(false);
        dateFieldKey.set(new Date().getTime());

        switch (statusCode) {
            case 204:
                stepForward();
                break;
            case 400:
                const invalidLastName = hasInvalidField(response?.data.errors, 'lastName');
                const invalidDOB = hasInvalidField(response?.data.errors, 'dateOfBirth');
                const invalidZipOrCity = hasInvalidField(response?.data.errors, 'zipCodeOrCity');
                const invalidEmail = hasInvalidField(response?.data.errors, 'email');
                if (invalidLastName) {
                    setErrorKeyHeader('completeItems');
                    setErrorListKey('invalidLastName');
                    setAnalyticsErrorKey('invalidLastName');
                } else if (invalidDOB) {
                    setErrorKeyHeader('completeItems');
                    setErrorKey(null);
                    setErrorListKey('invalidDOB');
                    setAnalyticsErrorKey('invalidDOB');
                } else if (invalidZipOrCity) {
                    setErrorKeyHeader('completeItems');
                    setErrorListKey('invalidZipOrCity');
                    setAnalyticsErrorKey('invalidZipOrCity');
                } else if (invalidEmail) {
                    setErrorKeyHeader('completeItems');
                    setErrorListKey('invalidEmail');
                    setAnalyticsErrorKey('invalidEmail');
                } else {
                    setErrorKeyHeader('cantFind');
                    setErrorKey('checkInformation');
                    setAnalyticsErrorKey('checkInformation');
                }
                break;
            case 404:
                setErrorKeyHeader('cantFind');
                setErrorKey('checkInformation');
                setAnalyticsErrorKey('checkInformation');
                break;
            case 409:
                setErrorKeyHeader('alreadyHaveAccount');
                setErrorKey('getHelp');
                setAnalyticsErrorKey('getHelp');
                break;
            default:
                setErrorKeyHeader('cantProcess');
                setErrorKey('processInconvenience');
                setAnalyticsErrorKey('processInconvenience');
                break;
        }

    };

    const getApiErrorMessage = () => {
        if (!errorMessage)
            return null;
        return errorMessage;
    };

    const getBackButton = () => {
        return <>
            <Button
                onClick={stepBackward}
            >
                {translate('back')}
            </Button>
        </>;
    };

    const getNextButton = () => {
        return <>
            <Button
                variation={'solid'}
                type={'submit'}
                isLoading={isLoading}
            >
                {translate('next')}
            </Button>
        </>;
    };

    const getValidationAlertMessages = () => {
        const messages: JSX.Element[] = [];
        if (!validateEntireForm.get())
            return messages;
        if (lastName === '')
            messages.push(
                <ValidationAlertMessage
                    description={translate('enter_your_last_name')}
                    fieldReference={lastNameRef}
                    linkText={translate('last_name')}
                    componentHeading={header}
                />
            );
        if (dateOfBirth.month === '' || dateOfBirth.day === '' || !isValidYear(dateOfBirth.year))
            messages.push(
                <ValidationAlertMessage
                    description={translate('enter_your_dob')}
                    fieldReference={monthRef}
                    linkText={translate('date_of_birth')}
                    componentHeading={header}
                />
            );
        if (zipCodeOrCity === '')
            messages.push(
                <ValidationAlertMessage
                    description={translate('enter_your_zipcode_or_city')}
                    fieldReference={zipCodeRef}
                    linkText={translate('zip_code_or_city')}
                    componentHeading={header}
                />
            );
        if (emailAddress === '' && !noEmailAddressChecked)
            messages.push(
                <ValidationAlertMessage
                    description={translate('enter_email_or_select_do_not_have')}
                    fieldReference={emailAddressRef}
                    linkText={translate('email')}
                    componentHeading={header}
                />
            );
        if (confirmEmailAddress !== emailAddress && !noEmailAddressChecked)
            messages.push(
                <ValidationAlertMessage
                    description={translate('confirm_your_email_address')}
                    fieldReference={confirmEmailAddressRef}
                    linkText={translate('confirm_email_address')}
                    componentHeading={header}
                />
            );
        if (!certificationChecked)
            messages.push(
                <ValidationAlertMessage
                    description={translate('check_to_certify_your_information')}
                    fieldReference={certificationCheckedRef}
                    linkText={translate('certification_statement')}
                    componentHeading={header}
                />
            );
        return messages;
    };

    const handleNextButtonClick = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setSubmitAttempted(true);
        resetErrors();
        if (isLoading) {
            return;
        }
        setIsLoading(true);
        conversion.attempt();
        const errorFieldRef = validateForm();
        if (errorFieldRef != null) {
            scrollIntoViewAndFocus(errorFieldRef);
            setIsLoading(false);
            return;
        }
        callCreateAccount();
    };

    const resetErrors = () => {
        setErrorKey(null);
        setErrorKeyHeader(null);
        setErrorListKey(null);
        lastNameError.set(false);
        zipCodeOrCityError.set(false);
        emailAddressError.set(false);
        emailAddressError.set(false);
        confirmEmailAddressError.set(false);
    };

    const updateCertificationChecked: ChangeEventHandler = (event) => {
        setCertificationTouched(true);
        if (event.type === 'change') {
            updateData({ certificationChecked: !certificationChecked });
        }
        if (validateEntireForm.get())
            validateForm();
    };

    const updateConfirmEmailAddress = (value: string, doImmediateValidation = false) => {
        const newValue = value.trimStart();
        updateData({ confirmEmailAddress: newValue });
        if (!doImmediateValidation && !validateEntireForm.get())
            return;
        validateConfirmEmailAddress();
        if (validateEntireForm.get())
            validateForm();
    };

    const updateDateOfBirth = (dateObject: DateObject) => {
        updateData({ dateOfBirth: dateObject });
        if (validateEntireForm.get())
            validateForm();
    };

    const updateEmailAddress = (value: string, doImmediateValidation = false) => {
        const newValue = value.trimStart();
        updateData({ emailAddress: newValue });
        if (!doImmediateValidation && !validateEntireForm.get())
            return;
        validateEmailAddress();
        if (validateEntireForm.get())
            validateForm();
    };

    const updateLastName = (value: string, doImmediateValidation = false) => {
        const newValue = value.trimStart();
        updateData({ lastName: newValue });
        if (!doImmediateValidation && !validateEntireForm.get())
            return;
        validateLastName();
        if (validateEntireForm.get())
            validateForm();
    };

    const updateNoEmailAddressChecked: ChangeEventHandler<HTMLInputElement> = (event) => {
        if (event.type === 'change') {
            if (!noEmailAddressChecked) {
                if (emailAddress !== '')
                    updateData({ emailAddress: '' });
                if (emailAddressError.get() !== null)
                    emailAddressError.set(false);
                if (confirmEmailAddress !== '')
                    updateData({ confirmEmailAddress: '' });
                if (confirmEmailAddressError.get() !== null)
                    confirmEmailAddressError.set(false);
            }
            updateData({ noEmailAddressChecked: !noEmailAddressChecked });
        }
        if (validateEntireForm.get())
            validateForm();
    };

    const updateZipCode = (value: string, doImmediateValidation = false) => {
        updateData({ zipCodeOrCity: value });
        if (!doImmediateValidation && !validateEntireForm.get())
            return;
        validateZipCode();
        if (validateEntireForm.get())
            validateForm();
    };

    const validateConfirmEmailAddress = () => {
        const isValid = noEmailAddressChecked || (confirmEmailAddress !== '' && confirmEmailAddress === emailAddress);
        if (confirmEmailAddressError.get() === isValid)
            confirmEmailAddressError.set(!isValid);
        return isValid;
    };

    const validateEmailAddress = () => {
        const isValid = noEmailAddressChecked || emailAddress !== '';
        if (emailAddressError.get() === isValid)
            emailAddressError.set(!isValid);
        return isValid;
    };

    const validateForm = (): HTMLElement | null | undefined => {
        if (!validateEntireForm.get())
            validateEntireForm.set(true);
        let errorFieldRef = null;
        if (!validateLastName())
            errorFieldRef = lastNameRef;
        if (dateOfBirth.month === '' && !errorFieldRef)
            errorFieldRef = monthRef;
        if (dateOfBirth.day === '' && !errorFieldRef)
            errorFieldRef = dayRef;
        if (!isValidYear(dateOfBirth.year) && !errorFieldRef)
            errorFieldRef = yearRef;
        if (!validateZipCode() && !errorFieldRef)
            errorFieldRef = zipCodeRef;
        if (!validateEmailAddress() && !errorFieldRef)
            errorFieldRef = emailAddressRef;
        if (!validateConfirmEmailAddress() && !errorFieldRef)
            errorFieldRef = confirmEmailAddressRef;
        if (!certificationChecked && !errorFieldRef)
            errorFieldRef = certificationCheckedRef;
        const messages = getValidationAlertMessages();
        if (messages.length) {
            const errorString = messages.map(x => x.props.description).join(', ');
            conversion.failValidationIntermediate(errorString);
        }
        return messages.length ? errorFieldRef : null;
    };

    const validateLastName = () => {
        const isValid = lastName !== '';
        if (lastNameError.get() === isValid)
            lastNameError.set(!isValid);
        return isValid;
    };

    const validateZipCode = () => {
        const isValid = zipCodeOrCity !== '';
        if (zipCodeOrCityError.get() === isValid)
            zipCodeOrCityError.set(!isValid);
        return isValid;
    };

    function getLastNameError() {
        if (lastNameError.get()) {
            return translate('enter_your_last_name')
        }
        if (errorListKey === 'invalidLastName') {
            return translate('check_last_name_try_again');
        }
        return null;
    }

    function getEmailError() {
        if (emailAddressError.get()) {
            return translate('enter_email_or_select_do_not_have');
        }
        if (errorListKey === 'invalidEmail') {
            return translate('check_email_address');
        }
        return null;
    }

    function getZipError() {
        if (zipCodeOrCityError.get()) {
            return translate('enter_your_zipcode_or_city');
        }
        if (errorListKey === 'invalidZipOrCity') {
            return translate('check_zip_try_again');
        }
        return null;
    }

    const getCertificationError = () => {
        if (!certificationTouched && !submitAttempted) {
            return null;
        }
        return certificationChecked ? null : translate('check_to_certify_your_information');
    }

    return <>
        <SecurityNoticeDialog/>
        <form onSubmit={handleNextButtonClick} method="POST">
            <fieldset style={{
                border: 0,
                margin: 0,
                padding: 0,
            }}>
                <Div isRow>
                    <Div cols={12}>
                        <legend>
                            <H2 className={'ds-u-margin-top--1 ds-u-margin-bottom--3'}>
                                <TranslatedSpan english={'enter_your_information'}/>
                            </H2>
                        </legend>
                        <AllFieldsRequired />
                        <div ref={errorContainerRef} tabIndex={-1}>
                            {(getApiErrorMessage() || apiErrorMessageList.length > 0) && getValidationAlertMessages().length === 0 &&
                            <ErrorAlert
                                heading={errorAlertHeading}
                                errorMessage={getApiErrorMessage()}
                                validationMessages={apiErrorMessageList} />}
                            {getValidationAlertMessages().length !== 0 &&
                                <ErrorAlert
                                    headingId={'validationAlertHeader'}
                                    heading={translate('complete_items_to_create_account')}
                                    validationMessages={getValidationAlertMessages()} />}
                        </div>
                    </Div>
                </Div>
                <LastNameSection
                    errorMessage={getLastNameError()}
                    inputRef={setLastNameRef}
                    lastNameVal={lastName}
                    onLastNameBlur={updateLastName}
                    onLastNameChange={updateLastName}
                    onSuffixChange={newSuffix => updateData({ suffix: newSuffix })}
                    suffixVal={suffix}
                />
                <HorizontalRule />
                <Div isRow>
                    <Div cols={12}>
                        <DateFields
                            date={dateOfBirth}
                            dayFieldRef={setDayRef}
                            forceValidation={validateEntireForm.get()}
                            hint={translate('use_format_mmddyyyy')}
                            key={dateFieldKey.get()}
                            label={translate('date_of_birth')}
                            monthFieldRef={setMonthRef}
                            onBlur={date => updateDateOfBirth(date)}
                            onChange={date => updateDateOfBirth(date)}
                            yearFieldRef={setYearRef}
                            errorMessage={translate("enter_your_dob")}
                        />
                    </Div>
                </Div>
                <HorizontalRule />
                <Div isRow>
                    <Div
                        cols={12} colsMD={7} colsLG={4} colsXL={3}
                        paddingRight={1}
                    >
                        <TextField
                            errorMessage={getZipError()}
                            inputRef={setZipCodeRef}
                            label={translate('zip_code_or_city')}
                            name={'zipCode'}
                            onBlur={event => updateZipCode(event.currentTarget.value.trim(), is.to.be.validated)}
                            onChange={event => updateZipCode(event.currentTarget.value)}
                            value={zipCodeOrCity}
                        />
                    </Div>
                </Div>
                <HorizontalRule />
                <Div isRow>
                    <Div
                        cols={12} colsMD={8} colsLG={6} colsXL={5}
                        paddingRight={1}
                    >
                        <TextField
                            disabled={!!noEmailAddressChecked}
                            errorMessage={getEmailError()}
                            inputRef={setEmailAddressRef}
                            label={translate('email_address')}
                            name={'emailAddress'}
                            onBlur={event => updateEmailAddress(event.currentTarget.value, is.to.be.validated)}
                            onChange={event => updateEmailAddress(event.currentTarget.value)}
                            value={emailAddress}
                        />
                    </Div>
                </Div>
                <Div isRow>
                    <Div
                        cols={12} colsMD={8} colsLG={6} colsXL={5}
                        paddingRight={1}
                    >
                        <TextField
                            disabled={!!noEmailAddressChecked}
                            errorMessage={confirmEmailAddressError.get() ? translate('reenter_email_address') : null}
                            inputRef={setConfirmEmailAddressRef}
                            label={translate('confirm_email_address')}
                            name={'confirmEmailAddress'}
                            onBlur={event => updateConfirmEmailAddress(event.currentTarget.value, is.to.be.validated)}
                            onChange={event => updateConfirmEmailAddress(event.currentTarget.value)}
                            value={confirmEmailAddress}
                        />
                    </Div>
                </Div>
                <Div isRow>
                    <Div cols={12} paddingTop={2}>
                        <Checkbox
                            checked={noEmailAddressChecked}
                            label={
                                <>
                                    <span className="ds-u-font-size--base" style={{ textTransform: 'none' }}>
                                        {translate('not_have_an_email_address')}
                                    </span>
                                    <p className="ds-u-margin-top--1 ds-u-margin-bottom--0 ds-u-font-weight--normal ds-u-sans ds-u-font-size--base" style={{ textTransform: 'none' }}>
                                        {translate('we_will_send_info_by_mail')}
                                    </p>
                                </>
                            }
                            className={'ds-u-margin-top--0'}
                            name={'noEmailAddressCheckbox'}
                            onBlur={event => updateNoEmailAddressChecked(event)}
                            onChange={event => updateNoEmailAddressChecked(event)}
                            title={translate('do_not_have_email_address')}
                            type={'checkbox'}
                            value={'noEmailAddress'}
                        >
                        </Checkbox>
                    </Div>
                </Div>
                <HorizontalRule marginBottom={2} />
                <Div isRow>
                    <Div cols={12} paddingTop={2}>
                        <Checkbox
                            aria-invalid={(!!getCertificationError()).toString()}
                            checked={certificationChecked}
                            label={
                                <H3 style={{ textTransform: 'none' }}>
                                    {translate('agree_to_statement_below')}
                                </H3>
                            }
                            className={'ds-u-margin-top--0 ds-u-measure--wide'}
                            errorMessage={getCertificationError()}
                            inputRef={setCertificationCheckedRef}
                            name={'certificationCheckbox'}
                            onBlur={() => setCertificationTouched(true)}
                            onChange={event => updateCertificationChecked(event)}
                            title={translate('check_box_to_certify_this_information')}
                            type={'checkbox'}
                            value={'certified'}
                        >
                        </Checkbox>
                    </Div>
                </Div>
            </fieldset>
            <StackingButtons
                marginTop={2}
                button1={getBackButton()}
                button2={getNextButton()}
            />
        </form>
    </>;
};

export default YourBasicInformation;
